import { Button, Grid, makeStyles, Paper } from '@material-ui/core';
import PageHeader from './PageHeader';
import { observer } from 'mobx-react-lite';
import React, { useState, useContext, useEffect } from 'react';

import SwapInput, { SwapInputProps } from './SwapInput';
import { IBBTC, WIBBTC } from 'config/constants';
import { SwapState } from 'config/enums/swap-state.enum';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { StoreContext } from 'mobx/store-context';
import { BigNumber } from 'bignumber.js';
import { TokenBalance } from 'mobx/stores/UserStore';
import { Link, Typography } from '@mui/material';

interface LandingProps {
	title: string;
	subtitle?: string;
}

export interface Token {
	name: string;
	address: string;
}

const useStyles = makeStyles(theme => ({
	swapPaper: {
		marginTop: theme.spacing(8),
		padding: theme.spacing(4, 6)
	},
	inputContainer: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(2),
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		marginTop: theme.spacing(1.5),
		borderRadius: '.625rem'
	},
	inputDecorationText: {
		fontWeight: 'bold',
		color: theme.palette.text.primary
	},
	inputText: {
		fontSize: '1.5rem',
		[theme.breakpoints.down('xs')]: {
			fontSize: '1rem',
			marginTop: 'auto'
		}
	},
	inputDisplayContainer: {
		height: '100%',
		alignItems: 'flex-end'
	},
	inputHeaderText: {
		color: theme.palette.text.primary,
		fontWeight: 'bolder',
		fontSize: '1.5rem',
		[theme.breakpoints.down('xs')]: {
			fontSize: '1rem',
			marginTop: 'auto'
		}
	},
	swapIcon: {
		fontSize: '4rem !important',
		marginTop: theme.spacing(4)
	},
	wrapButton: {
		marginTop: theme.spacing(2)
	},
	ppfsText: {
		marginTop: `${theme.spacing(4)}px !important`,
		textAlign: 'center'
	},
	linkContainer: {
		marginTop: theme.spacing(2)
	},
	link: {
		color: '#F2A52B !important',
		textDecoration: 'underline !important'
	}
}));

const Landing = observer((props: LandingProps) => {
	const classes = useStyles();
	const { title, subtitle } = props;
	const store = useContext(StoreContext);
	const {
		onboard: { address },
		user,
		contracts: { mint, redeem }
	} = store;

	const ibBTC = {
		address: IBBTC,
		name: 'ibBTC'
	};
	const wibBTC = {
		address: WIBBTC,
		name: 'wibBTC'
	};
	const defaultBalance = { displayBalance: '0', balance: new BigNumber(0) };

	const deriveBalance = (change: TokenBalance, swapData: SwapInputProps) => {
		if (swapData.token.name === 'ibBTC') {
			return change.balance.dividedBy(user.pricePerShare).multipliedBy(1e18);
		} else {
			return change.balance.multipliedBy(user.pricePerShare).dividedBy(1e18);
		}
	};

	const handleInputChange = (change: TokenBalance, state: SwapState) => {
		if (state === SwapState.To) {
			setSwapToData({ ...swapToData, swapAmount: change });
			const derivedAmount = deriveBalance(change, swapFromData);
			setSwapFromData({
				...swapFromData,
				swapAmount: {
					balance: derivedAmount,
					displayBalance: derivedAmount.dividedBy(1e18).toFixed(6, BigNumber.ROUND_HALF_FLOOR)
				}
			});
		} else {
			setSwapFromData({ ...swapFromData, swapAmount: change });
			const derivedAmount = deriveBalance(change, swapToData);
			setSwapToData({
				...swapToData,
				swapAmount: {
					balance: derivedAmount,
					displayBalance: derivedAmount.dividedBy(1e18).toFixed(6, BigNumber.ROUND_HALF_FLOOR)
				}
			});
		}
	};

	const defaultSwapFromData = {
		token: ibBTC,
		state: SwapState.From,
		updateInput: handleInputChange,
		swapAmount: defaultBalance
	};
	const defaultSwapToData = {
		token: wibBTC,
		state: SwapState.To,
		updateInput: handleInputChange,
		swapAmount: defaultBalance
	};

	const [swapFromData, setSwapFromData] = useState(defaultSwapFromData);
	const [swapToData, setSwapToData] = useState(defaultSwapToData);

	const changeSwapData = () => {
		const tempData = swapFromData;
		setSwapFromData({
			token: swapToData.token,
			swapAmount: swapToData.swapAmount,
			updateInput: handleInputChange,
			state: SwapState.From
		});
		setSwapToData({
			token: tempData.token,
			swapAmount: tempData.swapAmount,
			updateInput: handleInputChange,
			state: SwapState.To
		});
	};

	useEffect(() => {
		setSwapToData(defaultSwapToData);
		setSwapFromData(defaultSwapFromData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [address]);

	const isIbbtc = () => {
		return swapFromData.token.name === 'ibBTC';
	};

	const handleMint = async () => {
		const mintAmount = swapFromData.swapAmount;
		await mint(mintAmount, user.getTokenBalance(IBBTC).balance);
		await user.reloadBalances();
	};

	const handleBurn = async () => {
		const burnAmount = swapFromData.swapAmount;
		console.log('amount:', burnAmount.balance.dividedBy(1e18).toString());
		await redeem(burnAmount, user.getTokenBalance(WIBBTC).balance);
		await user.reloadBalances();
	};

	return (
		<>
			<Grid container spacing={1} justifyContent="center">
				<Grid justifyContent="center" item container xs={12}>
					<Grid item xs={6}>
						<PageHeader title={title} subtitle={subtitle} />
					</Grid>
				</Grid>
				<Grid item xs={6} container direction="column">
					<Grid className={classes.linkContainer} container direction="column">
						<Link
							className={classes.link}
							target="_blank"
							rel="noreferrer"
							href={'https://curve.fi/factory/60/deposit'}
						>
							Deposit to Curve
						</Link>
						<Link
							className={classes.link}
							target="_blank"
							rel="noreferrer"
							href={'https://app.badger.com/vault/convex-ibbtc-crvsbtc'}
						>
							Deposit Curve LP To Badger
						</Link>
					</Grid>

					<Paper className={classes.swapPaper}>
						<SwapInput
							token={swapFromData.token}
							state={swapFromData.state}
							updateInput={handleInputChange}
							swapAmount={swapFromData.swapAmount}
						/>
						<Grid justifyContent="center" container>
							<span onClick={changeSwapData}>
								<SwapVertIcon className={classes.swapIcon} />
							</span>
						</Grid>
						<SwapInput
							token={swapToData.token}
							state={swapToData.state}
							updateInput={handleInputChange}
							swapAmount={swapToData.swapAmount}
						/>
						<Typography className={classes.ppfsText}>
							wibBTC:ibBTC Ratio: {user.pricePerShare.dividedBy(1e18).toFixed(6)}
						</Typography>
						<Grid justifyContent="center" container>
							<Button
								onClick={isIbbtc() ? handleMint : handleBurn}
								disabled={!address}
								className={classes.wrapButton}
								variant="contained"
							>
								{swapFromData.token.name === 'ibBTC' ? 'Wrap ibBTC' : 'Unwrap wibBTC'}
							</Button>
						</Grid>
					</Paper>
				</Grid>
			</Grid>
		</>
	);
});

export default Landing;
